// Components
// Libs
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';

// API
import deleteWorksite from '@/common/api/worksites/deleteWorksite';
import postWorksite from '@/common/api/worksites/postWorksite';
import putWorksite from '@/common/api/worksites/putWorksite';
import Snack from '@/common/components/molecules/Snack/Snack';

const useWorksite = ({ disabled, isSuccess, isLoading }) => {
  const { t } = useTranslation();

  const {
    mutate: mutatePostWorksite,
    isLoading: isLoadingPostWorksite,
    isSuccess: isSuccessPostWorksite,
  } = useMutation(postWorksite);
  /**
   * Callback given to children component to create worksite
   * @param {object} values (Formik)
   * @param {object} actions (Formik)
   * @param {function} onSuccess
   */
  const onCreateWorksite = useCallback(
    ({ values, actions, onSuccess = () => {} }) => {
      mutatePostWorksite(
        { ...values },
        {
          onSuccess: (data) => {
            onSuccess(data);

            toast.success(
              <Snack message={t('common:messages:forms.success.default')} />,
            );
          },
          onError: (error) => {
            switch (error.response.status) {
              case 500:
                toast.error(
                  <Snack
                    message={t('common:messages:forms.errors.server_error')}
                  />,
                );
                break;
              case 503:
                toast.error(
                  <Snack
                    message={t(
                      'common:messages:forms.errors.worksite_code_already_exist',
                    )}
                  />,
                );
                break;
              default:
                if (error.response.data.errors) {
                  actions.setErrors(error.response.data.errors);
                }

                toast.error(
                  <Snack message={t('common:messages:forms.errors.default')} />,
                );
            }
          },
        },
      );
    },
    [mutatePostWorksite, t],
  );

  const {
    mutate: mutateDeleteWorksite,
    isLoading: isLoadingDeleteWorksite,
    isSuccess: isSuccessDeleteWorksite,
  } = useMutation(deleteWorksite);
  /**
   * Callback given to children component to delete worksite
   * @param {object} values (Formik)
   * @param {object} actions (Formik)
   * @param {function} onSuccess
   */
  const onDeleteWorksite = useCallback(
    ({ code, onSuccess = () => {} }) => {
      mutateDeleteWorksite(code, {
        onSuccess: (data) => {
          onSuccess(data);

          toast.success(
            <Snack message={t('common:messages:forms.success.default')} />,
          );
        },
        onError: (error) => {
          if (error.response.status === 500) {
            toast.error(
              <Snack
                message={t('common:messages:forms.errors.server_error')}
              />,
            );
          } else {
            toast.error(
              <Snack message={t('common:messages:forms.errors.default')} />,
            );
          }
        },
      });
    },
    [mutateDeleteWorksite, t],
  );

  const {
    mutate: mutatePutWorksite,
    isLoading: isLoadingPutWorksite,
    isSuccess: isSuccessPutWorksite,
  } = useMutation(putWorksite);
  /**
   * Callback given to children component to update worksite
   * @param {object} values (Formik)
   * @param {object} actions (Formik)
   * @param {function} onSuccess
   */
  const onUpdateWorksite = useCallback(
    ({ values, actions, onSuccess = () => {} }) => {
      mutatePutWorksite(
        { ...values },
        {
          onSuccess: (data) => {
            onSuccess(data);

            toast.success(
              <Snack message={t('common:messages:forms.success.default')} />,
            );
          },
          onError: (error) => {
            if (error.response.status === 500) {
              toast.error(
                <Snack
                  message={t('common:messages:forms.errors.server_error')}
                />,
              );
            } else {
              if (error.response.data.errors) {
                actions.setErrors(error.response.data.errors);
              }

              toast.error(
                <Snack message={t('common:messages:forms.errors.default')} />,
              );
            }
          },
        },
      );
    },
    [mutatePutWorksite, t],
  );

  /**
   * Check if action is disabled
   * @type {boolean}
   */
  const isDisabled = useMemo(
    () => ({
      loading:
        isLoading ||
        isLoadingDeleteWorksite ||
        isLoadingPostWorksite ||
        isLoadingPutWorksite,
      disabled:
        disabled ||
        isLoading ||
        isLoadingDeleteWorksite ||
        isLoadingPostWorksite ||
        isLoadingPutWorksite,
    }),
    [
      disabled,
      isLoading,
      isLoadingDeleteWorksite,
      isLoadingPostWorksite,
      isLoadingPutWorksite,
    ],
  );

  return {
    isDisabled,
    isSuccess:
      isSuccess ||
      isSuccessDeleteWorksite ||
      isSuccessPostWorksite ||
      isSuccessPutWorksite,
    onCreateWorksite,
    onDeleteWorksite,
    onUpdateWorksite,
  };
};

export default useWorksite;
